import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NAV_LINKS = [
  { label: '首页', to: '/' },
  { label: '电视剧', to: '/category/tv' },
  { label: '电影', to: '/category/movie' },
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const submitSearch = () => {
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : '/search');
  };

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="h-16 px-4 md:px-8 flex items-center justify-between gap-4">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 shrink-0 group" aria-label="年轻的岳母 首页">
          <span className="w-9 h-9 rounded-md bg-primary-600 text-background-50 flex items-center justify-center text-xl">
            <i className="ri-film-line" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-bold text-lg text-primary-700">年轻的岳母</span>
            <span className="hidden sm:block text-[10px] tracking-[0.2em] text-foreground-400 uppercase">
              HD Cinema
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                isActive(link.to)
                  ? 'text-primary-700 font-medium bg-primary-50'
                  : 'text-foreground-700 hover:text-primary-700 hover:bg-background-100'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Search + mobile toggle */}
        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden sm:flex items-center bg-background-100 border border-background-200 rounded-full pl-3 pr-1 h-10 w-48 lg:w-64 focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-100 transition-all">
            <i className="ri-search-line text-foreground-400 text-base" aria-hidden="true"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitSearch();
              }}
              placeholder="搜索剧名或演员"
              className="flex-1 bg-transparent text-sm px-2 outline-none text-foreground-900 placeholder:text-foreground-400"
              aria-label="搜索影视"
            />
            <button
              type="button"
              onClick={submitSearch}
              className="h-8 w-8 shrink-0 rounded-full bg-accent-500 text-background-50 flex items-center justify-center hover:bg-accent-600 transition-colors cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </button>
          </div>
          <button
            type="button"
            onClick={submitSearch}
            className="sm:hidden h-10 w-10 rounded-full bg-background-100 border border-background-200 text-foreground-700 flex items-center justify-center cursor-pointer hover:text-primary-700"
            aria-label="搜索"
          >
            <i className="ri-search-line" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden h-10 w-10 rounded-full bg-background-100 border border-background-200 text-foreground-700 flex items-center justify-center cursor-pointer hover:text-primary-700"
            aria-label="菜单"
            aria-expanded={mobileOpen}
          >
            <i className={mobileOpen ? 'ri-close-line text-lg' : 'ri-menu-line text-lg'} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 space-y-3">
          <div className="flex items-center bg-background-100 border border-background-200 rounded-full pl-3 pr-1 h-11 focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitSearch();
              }}
              placeholder="搜索剧名或演员"
              className="flex-1 bg-transparent text-sm px-2 outline-none text-foreground-900 placeholder:text-foreground-400"
              aria-label="搜索影视"
            />
            <button
              type="button"
              onClick={submitSearch}
              className="h-8 w-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </button>
          </div>
          <nav className="flex flex-col">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-3 rounded-md text-sm ${
                  isActive(link.to)
                    ? 'text-primary-700 font-medium bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}